import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Users, Clock, MapPin, Ship, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatMoney } from "../lib/pricing";
import YachtGallery from "../components/YachtGallery";
import YachtCalendar from "../components/YachtCalendar";
import ContactPickerModal, {
  useHasPublicContact,
} from "../components/ContactPickerModal";

export default function YachtDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [yacht, setYacht] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const { hasAny: hasContact, settings } = useHasPublicContact();

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    api.get(`/yachts/${slug}`)
      .then((r) => !ignore && setYacht(r.data))
      .catch((e) => !ignore && setError(formatApiErrorDetail(e)))
      .finally(() => !ignore && setLoading(false));
    return () => {
      ignore = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="hbe-container py-20 text-hbe-ink/55" data-testid="yacht-loading">
        Loading…
      </div>
    );
  }
  if (error || !yacht) {
    return (
      <div className="hbe-container py-20" data-testid="yacht-error">
        <h1 className="font-serif text-3xl">Yacht not found</h1>
        <p className="mt-3 text-hbe-ink/70">{error || "We couldn't find this yacht."}</p>
        <Link to="/yachts" className="hbe-btn-primary mt-6 inline-flex">
          Back to yachts
        </Link>
      </div>
    );
  }

  function goBook() {
    const q = selectedDate ? `?date=${selectedDate}` : "";
    navigate(`/book/${yacht.slug}${q}`);
  }

  return (
    <div data-testid="yacht-detail-page">
      <section className="hbe-container pt-8 md:pt-12">
        <YachtGallery images={yacht.images} yachtName={yacht.name} />
      </section>

      <section className="hbe-container grid gap-14 lg:grid-cols-[1fr_360px] pt-10 md:pt-14 pb-24">
        <div>
          <div className="hbe-eyebrow">Miami · Private charter</div>
          <h1 className="mt-3 font-serif text-4xl md:text-5xl">{yacht.name}</h1>

          <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 text-sm text-hbe-ink/75">
            <span className="inline-flex items-center gap-2" data-testid="fact-capacity">
              <Users size={16} className="text-hbe-accent" /> Up to {yacht.capacity} guests
            </span>
            <span className="inline-flex items-center gap-2" data-testid="fact-min">
              <Clock size={16} className="text-hbe-accent" /> {yacht.minimum_duration_hours}-hour minimum
            </span>
            <span className="inline-flex items-center gap-2" data-testid="fact-location">
              <MapPin size={16} className="text-hbe-accent" /> Miami
            </span>
            <span className="inline-flex items-center gap-2" data-testid="fact-from">
              <Ship size={16} className="text-hbe-accent" /> From {formatMoney(yacht.base_price)}
            </span>
          </div>

          {yacht.description && (
            <p className="mt-8 text-hbe-ink/80 max-w-2xl" data-testid="yacht-description">
              {yacht.description}
            </p>
          )}

          {yacht.amenities?.length > 0 && (
            <div className="mt-10">
              <h2 className="font-serif text-2xl">On board</h2>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2 max-w-xl">
                {yacht.amenities.map((a) => (
                  <li
                    key={a}
                    className="flex items-center gap-2 text-hbe-ink/80"
                    data-testid={`amenity-${a}`}
                  >
                    <Check size={16} className="text-hbe-accent" /> {a}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-14 pt-10 border-t border-hbe-line">
            <h2 className="font-serif text-2xl">Availability</h2>
            <p className="mt-2 text-sm text-hbe-ink/70 max-w-lg">
              Pick a date to start. Booking window opens 24 hours ahead and
              runs six months out.
            </p>
            <div className="mt-8 max-w-md">
              <YachtCalendar
                yachtKey={yacht.slug}
                value={selectedDate}
                onChange={setSelectedDate}
              />
            </div>
          </div>
        </div>

        {/* Sticky booking card (desktop) */}
        <aside
          className="hidden lg:block sticky top-24 self-start hbe-card p-6"
          data-testid="booking-card-desktop"
        >
          <div className="hbe-eyebrow">From</div>
          <div className="mt-1 font-serif text-3xl text-hbe-primary">
            {formatMoney(yacht.base_price)}
            <span className="text-base text-hbe-ink/60 font-sans"> / {yacht.minimum_duration_hours} hours</span>
          </div>
          {yacht.additional_hour_price != null && (
            <div className="mt-1 text-xs text-hbe-ink/60">
              +{formatMoney(yacht.additional_hour_price)} per additional hour
            </div>
          )}
          <div
            className="mt-3 text-xs leading-relaxed"
            style={{ color: "#557A95" }}
            data-testid="deposit-note-desktop"
          >
            $500 deposit today, {formatMoney(Math.max(0, yacht.base_price - 500))} on your charter day.<br />
            Total {formatMoney(yacht.base_price)} — deposit is applied.
          </div>
          <div className="mt-5 text-sm text-hbe-ink/75">
            {selectedDate ? (
              <span data-testid="booking-card-selected-date">
                Selected: <span className="text-hbe-primary font-medium">{selectedDate}</span>
              </span>
            ) : (
              "Pick a date from the calendar to continue."
            )}
          </div>
          <button
            type="button"
            onClick={goBook}
            className="hbe-btn-primary w-full mt-5"
            data-testid="booking-card-cta"
          >
            Book this yacht
          </button>
          {hasContact && (
            <button
              type="button"
              onClick={() => setContactOpen(true)}
              className="w-full text-sm text-hbe-accent hover:text-hbe-primary underline underline-offset-4 decoration-1 font-normal py-2 mt-2 transition-colors"
              data-testid="booking-card-contact-cta"
            >
              Or book by phone or WhatsApp
            </button>
          )}
          <p className="mt-4 text-[11px] text-hbe-ink/45">
            $500 deposit today. Balance on the day. Non-refundable deposit.
          </p>
        </aside>
      </section>

      {/* Mobile sticky bottom bar */}
      <div
        className="lg:hidden fixed bottom-0 inset-x-0 border-t border-hbe-line bg-white z-30"
        data-testid="booking-card-mobile"
      >
        <div className="hbe-container py-3 flex items-center justify-between gap-3">
          <div>
            <div className="text-xs text-hbe-ink/55">From</div>
            <div className="font-serif text-lg text-hbe-primary leading-tight">
              {formatMoney(yacht.base_price)}{" "}
              <span className="text-xs text-hbe-ink/60 font-sans">
                / {yacht.minimum_duration_hours}h
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasContact && (
              <button
                type="button"
                onClick={() => setContactOpen(true)}
                className="hbe-btn-ghost !py-2 !px-4 !text-[0.85rem] !min-h-[38px]"
                aria-label="Book by phone or WhatsApp"
                data-testid="booking-card-mobile-contact-cta"
              >
                Call
              </button>
            )}
            <button
              type="button"
              onClick={goBook}
              className="hbe-btn-primary flex-1 max-w-[220px]"
              data-testid="booking-card-mobile-cta"
            >
              Book
            </button>
          </div>
        </div>
      </div>

      <ContactPickerModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        settings={settings}
        title="Book by phone or WhatsApp"
        subtitle="Prefer a real person? Reach us directly — we'll confirm your date on the spot."
      />
    </div>
  );
}
